/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { cssMap, jsx } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import { token } from '@atlaskit/tokens';

import { metricSelectionMessages } from '../../../../utils/metricSelectionMessages';
import type { SelectedMetric } from '../../../../utils/orgMetrics';
import { SpacingWrapper } from '../../SpacingWrapper';

import tempIcon from './assets/temp-icon.svg';
import { messages } from './messages';

const styles = cssMap({
	section: {
		maxWidth: '100%',
		paddingTop: token('space.0'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		paddingBottom: '7.5rem',
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.400'),
		// NO ADS token exists for 88px
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		paddingInline: '88px',
		'@media (max-width: 30rem)': {
			paddingInline: token('space.0'),
		},
	},
	heading: {
		margin: 0,
		maxWidth: '1200px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontFamily: "'Charlie Display', 'Charlie Text', sans-serif",
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: 1.5,
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontWeight: 400,
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		letterSpacing: '0.5px',
		color: token('color.text.subtle'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontSize: '2rem',
		'@media (max-width: 48rem)': {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
			fontSize: '32px',
		},
		'@media (max-width: 30rem)': {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
			fontSize: '24px',
		},
	},
	row: {
		display: 'grid',
		maxWidth: '1200px',
		width: '100%',
		gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
		gap: token('space.300'),
		'@media (max-width: 64rem)': {
			gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
		},
		'@media (max-width: 30rem)': {
			gridTemplateColumns: '1fr',
		},
	},
	card: {
		height: '240px',
		backgroundColor: token('color.background.disabled'),
		borderRadius: token('radius.medium'),
		paddingInline: token('space.400'),
		paddingBlock: token('space.400'),
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		minWidth: '0',
	},
	statIcon: {
		height: '40px',
		marginBottom: 'auto',
	},
	value: {
		margin: 0,
		color: token('color.text'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontSize: '2.25rem',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontFamily: token('font.family.code'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontWeight: 400,
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: 'normal',
		marginBottom: token('space.100'),
	},
	label: {
		margin: 0,
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: token('color.text.subtle'),
		font: token('font.heading.xsmall'),
		fontFamily: token('font.family.code'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontWeight: 600,
		// maxWidth: '70%',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		letterSpacing: '0.0625rem',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		textTransform: 'uppercase',
		display: '-webkit-box',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- webkit line clamp for multiline truncation
		WebkitLineClamp: 2 as any,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- webkit box orient for multiline truncation
		WebkitBoxOrient: 'vertical' as any,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
});

type GraphGrowthSectionProps = {
	stats?: readonly SelectedMetric[];
};

export function GraphGrowthSection({ stats }: GraphGrowthSectionProps): JSX.Element | null {
	const { formatNumber } = useIntl();

	if (!stats?.length) {
		return null;
	}

	return (
		<SpacingWrapper>
			<section css={styles.section}>
				{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
				<p css={styles.heading}>
					<FormattedMessage {...messages.heading} />
				</p>
				<div css={styles.row}>
					{stats?.map((stat) => {
						const formattedValues = Object.fromEntries(
							Object.entries(stat.values).map(([key, value]) => [
								key,
								typeof value === 'number'
									? formatNumber(value, {
											notation: 'compact',
											maximumFractionDigits: 2,
										})
									: value,
							]),
						);
						return (
							<div key={stat.copyKey} css={styles.card}>
								<img src={tempIcon} css={styles.statIcon} alt="temp icon" />
								{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
								<p css={styles.value}>{formatNumber(stat.value)}</p>
								{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
								<p css={styles.label}>
									<FormattedMessage
										{...metricSelectionMessages[
											stat.copyKey as keyof typeof metricSelectionMessages
										]}
										values={formattedValues}
									/>
								</p>
							</div>
						);
					})}
				</div>
			</section>
		</SpacingWrapper>
	);
}
